import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { type ColumnDef } from "@tanstack/react-table";
import {
  RefreshCw,
  Search,
  UserPlus,
  Shield,
  ShieldOff,
} from "lucide-react";
import { useUsers } from "@/hooks/use-users";
import { CreateUserDialog } from "@/components/users/CreateUserDialog";
import { PageHeader } from "@/components/shared/page-header";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { Avatar, AvatarFallback, Badge, Button, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@fox1ck-org/ui";

const PAGE_SIZE = 20;

function formatDate(timestamp: number) {
  return new Date(timestamp).toLocaleDateString("ru-RU", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

type UserRow = {
  id: string;
  username: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  emailVerified?: boolean;
  enabled: boolean;
  totp?: boolean;
  createdTimestamp: number;
};

const columns: ColumnDef<UserRow>[] = [
  {
    accessorKey: "username",
    header: "Пользователь",
    cell: ({ row }) => {
      const u = row.original;
      const displayName = u.firstName || u.lastName
        ? `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim()
        : u.username;
      const initial = (u.firstName?.[0] ?? u.username[0] ?? "?").toUpperCase();
      return (
        <Link to={`/users/${u.id}`} className="flex items-center gap-3">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="text-xs">{initial}</AvatarFallback>
          </Avatar>
          <div>
            <div className="font-medium text-foreground hover:text-primary">{displayName}</div>
            <div className="text-xs text-muted-foreground">@{u.username}</div>
          </div>
        </Link>
      );
    },
  },
  {
    accessorKey: "email",
    header: "Почта",
    cell: ({ row }) => (
      <div className="flex items-center gap-1.5">
        <span className="text-muted-foreground">{row.original.email ?? "-"}</span>
        {row.original.emailVerified && (
          <span className="text-xs text-green-400" title="Подтверждена">&#10003;</span>
        )}
      </div>
    ),
  },
  {
    accessorKey: "enabled",
    header: "Статус",
    cell: ({ row }) =>
      row.original.enabled ? (
        <Badge variant="ok"><Shield className="h-3 w-3 mr-1" />Активна</Badge>
      ) : (
        <Badge variant="danger"><ShieldOff className="h-3 w-3 mr-1" />Отключён</Badge>
      ),
  },
  {
    accessorKey: "totp",
    header: "MFA",
    cell: ({ row }) => (
      <span
        className={`inline-block h-2.5 w-2.5 rounded-full ${row.original.totp ? "bg-green-400" : "bg-muted-foreground"}`}
        title={row.original.totp ? "MFA включена" : "MFA не настроена"}
      />
    ),
  },
  {
    accessorKey: "createdTimestamp",
    header: "Создан",
    cell: ({ row }) => <span className="text-muted-foreground">{formatDate(row.original.createdTimestamp)}</span>,
  },
];

export default function Users() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [enabledFilter, setEnabledFilter] = useState<boolean | undefined>(undefined);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading, refetch } = useUsers({
    search: debouncedSearch || undefined,
    first: page * PAGE_SIZE,
    max: PAGE_SIZE,
    enabled: enabledFilter,
  });

  const users = data?.users ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-6">
      <PageHeader title="Пользователи" description="Пользователи, группы и роли организации">
        <Button size="sm" onClick={() => setShowCreate(true)}>
          <UserPlus className="h-4 w-4" /> Новый пользователь
        </Button>
        <Button variant="ghost" size="icon" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </PageHeader>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Поиск по имени, почте или логину…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select
          value={enabledFilter === undefined ? "all" : String(enabledFilter)}
          onValueChange={(v) => {
            setEnabledFilter(v === "all" ? undefined : v === "true");
            setPage(0);
          }}
        >
          <SelectTrigger className="w-[130px]">
            <SelectValue placeholder="Все пользователи" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все пользователи</SelectItem>
            <SelectItem value="true">Включён</SelectItem>
            <SelectItem value="false">Отключён</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">{total} user{total !== 1 ? "s" : ""}</span>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={users}
        isLoading={isLoading}
        showPagination={false}
        emptyState={
          <EmptyState
            title={debouncedSearch ? "Никого не нашлось" : "Пользователей нет"}
            action={!debouncedSearch ? { label: "Новый пользователь", onClick: () => setShowCreate(true) } : undefined}
          />
        }
      />

      {/* Manual Pagination (server-side) */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Показаны {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} из {total}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>
              Назад
            </Button>
            <span className="text-sm text-muted-foreground">{page + 1} / {totalPages}</span>
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>
              Вперёд
            </Button>
          </div>
        </div>
      )}

      {showCreate && <CreateUserDialog onClose={() => setShowCreate(false)} />}
    </div>
  );
}
